import React, { useState } from 'react';
import { NavLink, Link } from 'react-router-dom';
import { FileDown, ShieldCheck, Globe, Menu, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
export function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const navLinkClass = ({
    isActive
  }: {
    isActive: boolean;
  }) => `transition-colors relative py-2 ${isActive ? 'text-federal-blue font-black' : 'text-tech-slate hover:text-federal-blue'}`;
  const mobileNavLinkClass = ({
    isActive
  }: {
    isActive: boolean;
  }) => `block py-4 text-lg font-bold border-b border-slate-100 ${isActive ? 'text-federal-blue' : 'text-tech-slate'}`;
  const activeIndicator = ({
    isActive
  }: {
    isActive: boolean;
  }) => isActive ? <span className="absolute -bottom-1 left-0 w-full h-0.5 bg-muted-gold" data-id="element-188" /> : null;
  const navItems = [{
    to: '/capabilities',
    label: 'Capabilities'
  }, {
    to: '/legacy',
    label: 'Our Legacy'
  }, {
    to: '/locations',
    label: 'Locations'
  }, {
    to: '/entity-data',
    label: 'Entity Data'
  }, {
    to: '/contact',
    label: 'Contact Us'
  }];
  return <header className="sticky top-0 z-50 w-full" data-id="element-189">
      {/* Top Utility Bar */}
      <div className="bg-federal-blue text-white py-2 px-4 md:px-8 border-b border-white/10" data-id="element-190">
        <div className="max-w-7xl mx-auto flex flex-wrap justify-between items-center text-[10px] md:text-xs font-medium tracking-wider uppercase" data-id="element-191">
          <div className="flex gap-4 md:gap-8" data-id="element-192">
            <span className="flex items-center gap-1.5 opacity-90" data-id="element-193">
              <ShieldCheck className="w-3 h-3 text-muted-gold" data-id="element-194" />
              Socio-economic Status: WOSB / EDWOSB
            </span>
            <span className="flex items-center gap-1.5 opacity-90" data-id="element-195">
              <Globe className="w-3 h-3 text-muted-gold" data-id="element-196" />
              Registered: SAM.gov / UEI Ready
            </span>
          </div>
          <div className="hidden md:block opacity-70" data-id="element-197">
            CAGE: 9XYL4 | UEI: ZK82N9L1M4P7
          </div>
        </div>
      </div>

      {/* Main Navigation */}
      <nav className="bg-white border-b border-slate-200 shadow-sm relative z-50" data-id="element-198">
        <div className="max-w-7xl mx-auto px-4 md:px-8 h-20 flex items-center justify-between" data-id="element-199">
          <Link to="/" className="flex items-center gap-2" onClick={() => setIsMenuOpen(false)} data-id="element-200">
            <div className="w-12 h-10 flex items-center justify-center overflow-hidden" data-id="element-201">
              <img src="https://cdn.magicpatterns.com/uploads/cMrkjmuEGCTQik26MvLHNF/ChatGPT_Image_Apr_14,_2026,_01_45_07_PM.png" alt="Codes & Cogs Logo" className="h-full w-auto object-contain" data-id="element-202" />
            </div>
            <div className="flex flex-col leading-tight" data-id="element-203">
              <span className="text-federal-blue font-black text-xl tracking-tighter uppercase" data-id="element-204">
                Codes & Cogs
              </span>
              <span className="text-tech-slate text-[10px] font-bold tracking-[0.2em] uppercase" data-id="element-205">
                Federal Solutions
              </span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8 text-sm font-semibold text-tech-slate" data-id="element-206">
            {navItems.map(item => <NavLink key={item.to} to={item.to} className={navLinkClass} data-id="element-207">
                {({
              isActive
            }) => <>
                    {item.label}
                    {activeIndicator({
                isActive
              })}
                  </>}
              </NavLink>)}
          </div>

          <div className="flex items-center gap-4" data-id="element-208">
            <button className="hidden sm:flex bg-muted-gold hover:bg-[#b38f4d] text-white px-5 py-2.5 rounded-sm font-bold text-sm items-center gap-2 transition-all shadow-md active:scale-95" data-id="element-209">
              <FileDown className="w-4 h-4" data-id="element-210" />
              <span className="hidden sm:inline" data-id="element-211">
                Download Capability Statement
              </span>
              <span className="sm:hidden" data-id="element-212">PDF</span>
            </button>

            {/* Mobile Menu Toggle */}
            <button className="lg:hidden p-2 text-federal-blue hover:bg-slate-50 rounded-md transition-colors" onClick={() => setIsMenuOpen(!isMenuOpen)} aria-label="Toggle menu" data-id="element-213">
              {isMenuOpen ? <X className="w-6 h-6" data-id="element-214" /> : <Menu className="w-6 h-6" data-id="element-215" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu Overlay */}
        <AnimatePresence data-id="element-216">
          {isMenuOpen && <motion.div initial={{
          opacity: 0,
          height: 0
        }} animate={{
          opacity: 1,
          height: 'auto'
        }} exit={{
          opacity: 0,
          height: 0
        }} transition={{
          duration: 0.3,
          ease: 'easeInOut'
        }} className="lg:hidden bg-white border-t border-slate-100 overflow-hidden shadow-xl" data-id="element-217">
              <div className="px-4 py-6 space-y-2" data-id="element-218">
                {navItems.map(item => <NavLink key={item.to} to={item.to} className={mobileNavLinkClass} onClick={() => setIsMenuOpen(false)} data-id="element-219">
                    {item.label}
                  </NavLink>)}
                <div className="pt-6 border-t border-slate-100" data-id="element-220">
                  <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-4" data-id="element-221">
                    Entity Identifiers
                  </p>
                  <div className="grid grid-cols-2 gap-4 text-[10px] font-bold text-federal-blue" data-id="element-222">
                    <div data-id="element-223">
                      <span className="text-slate-400 block mb-1" data-id="element-224">CAGE</span>
                      9XYL4
                    </div>
                    <div data-id="element-225">
                      <span className="text-slate-400 block mb-1" data-id="element-226">UEI</span>
                      ZK82N9L1M4P7
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>}
        </AnimatePresence>
      </nav>
    </header>;
}